import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type AnnouncementChannelConfig } from "@fitness-app/data-models/entities/TrainerConfig";

import { chatApi } from "~/store/initializeStore";

export interface AnnouncementChannelOverview {
  config: AnnouncementChannelConfig | null;
  totalActive: number;
  withChannel: number;
  clientsWithoutChannel: Array<{
    traineeId: string;
    userId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    productId: string | null;
    productName: string | null;
  }>;
}

export interface BackfillAnnouncementChannelsResult {
  processed: number;
  created: number;
  /** Clients still without a channel after this batch — repeat the call until zero. */
  remaining: number;
  failed: Array<{ traineeId: string; email: string; error: string }>;
}

const OVERVIEW_QUERY_KEY = ["announcementChannelOverview"];

export const useAnnouncementChannelOverview = (enabled: boolean) =>
  useQuery(
    OVERVIEW_QUERY_KEY,
    async (): Promise<AnnouncementChannelOverview> => {
      const response = await chatApi.get<AnnouncementChannelOverview>("/announcement-channel/overview");
      return response.data;
    },
    { enabled, staleTime: 30000 },
  );

export const useUpdateAnnouncementChannelConfig = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (config: AnnouncementChannelConfig & { applyToExisting?: boolean }) => {
      const response = await chatApi.put<{ status: string; updatedChannels: number }>(
        "/announcement-channel/config",
        config,
      );
      return response.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(OVERVIEW_QUERY_KEY),
    },
  );
};

export const useBackfillAnnouncementChannels = () => {
  const queryClient = useQueryClient();

  return useMutation(
    // The endpoint processes a bounded batch per request and reports `remaining`;
    // loop here until the tenant is covered so one click finishes the job.
    async (traineeIds?: string[]) => {
      const totals: BackfillAnnouncementChannelsResult = { processed: 0, created: 0, remaining: 0, failed: [] };

      for (;;) {
        const response = await chatApi.post<BackfillAnnouncementChannelsResult>("/announcement-channel/backfill", {
          ...(traineeIds?.length ? { traineeIds } : {}),
        });
        const batch = response.data;

        totals.processed += batch.processed;
        totals.created += batch.created;
        totals.failed.push(...batch.failed);
        totals.remaining = batch.remaining;

        // Failed targets stay "without a channel", so retrying them forever would loop.
        if (batch.remaining === 0 || batch.processed === 0 || batch.failed.length > 0) {
          return totals;
        }
      }
    },
    {
      onSuccess: () => queryClient.invalidateQueries(OVERVIEW_QUERY_KEY),
    },
  );
};
